import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import { productService } from '../../services/productService';
import { aiService } from '../../services/aiService';
import { triggerAutoReload } from '../../utils/autoReload';

const GroupVariationsTab = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [grouping, setGrouping] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [analyzingWithAI, setAnalyzingWithAI] = useState(false);
  const [detectedCount, setDetectedCount] = useState(0);
  const [activeTab, setActiveTab] = useState('automatic');

  const groupingTabs = [
    { id: 'automatic', label: 'Automático', icon: 'Zap', description: 'Agrupamento inteligente' },
    { id: 'color', label: 'Por Cor', icon: 'Palette', description: 'Variações de cor' },
    { id: 'size', label: 'Por Tamanho', icon: 'Maximize2', description: 'Variações de tamanho' },
    { id: 'brand', label: 'Por Marca', icon: 'Award', description: 'Mesma marca' },
    { id: 'category', label: 'Por Categoria', icon: 'Grid', description: 'Mesma categoria' }
  ];

  useEffect(() => {
    loadVariationGroups();
  }, [activeTab]);

  const loadVariationGroups = async () => {
    try {
      setLoading(true);
      const { products } = await productService?.getAll();

      const originalProducts = products?.filter(p => {
        if (p?.productType === 'kit' || p?.product_type === 'kit') return false;
        if (p?.metadata?.groupType) return false;
        return true;
      });

      let similarGroups = [];
      
      switch (activeTab) {
        case 'color':
          similarGroups = findGroupsByColor(originalProducts);
          break;
        case 'size':
          similarGroups = findGroupsBySize(originalProducts);
          break;
        case 'brand':
          similarGroups = findGroupsByBrand(originalProducts);
          break;
        case 'category':
          similarGroups = findGroupsByCategory(originalProducts);
          break;
        default:
          similarGroups = findSimilarProducts(originalProducts);
      }
      
      setGroups(similarGroups);
      
      const totalProductsInGroups = similarGroups?.reduce((sum, group) => {
        return sum + 1 + (group?.similar?.length || 0);
      }, 0);
      setDetectedCount(totalProductsInGroups);
    } catch (error) {
      console.error('Error loading variation groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const findGroupsByColor = (products) => {
    const colorKeywords = ['preto', 'branco', 'azul', 'vermelho', 'verde', 'amarelo', 'rosa', 'roxo', 'laranja', 'cinza', 'marrom', 'bege', 'black', 'white', 'blue', 'red', 'green', 'yellow', 'pink', 'purple', 'orange', 'gray', 'brown'];
    const groups = [];
    const processed = new Set();

    products?.forEach((product, index) => {
      if (processed?.has(product?.id)) return;

      const productName = product?.name?.toLowerCase();
      const productDesc = (product?.description || '')?.toLowerCase();
      const combinedText = `${productName} ${productDesc}`;
      const hasColorInName = colorKeywords?.some(color => combinedText?.includes(color));
      
      if (!hasColorInName) return;

      const similar = products?.filter((p, idx) => {
        if (idx === index || processed?.has(p?.id)) return false;
        const pName = p?.name?.toLowerCase();
        const pDesc = (p?.description || '')?.toLowerCase();
        const pCombined = `${pName} ${pDesc}`;
        const hasColor = colorKeywords?.some(color => pCombined?.includes(color));
        if (!hasColor) return false;
        
        // Remove color words and compare base names
        const baseName1 = productName?.replace(new RegExp(colorKeywords?.join('|'), 'gi'), '')?.trim();
        const baseName2 = pName?.replace(new RegExp(colorKeywords?.join('|'), 'gi'), '')?.trim();
        
        // Also check category and brand similarity
        const sameCategory = p?.category === product?.category;
        const sameBrand = p?.brand === product?.brand;
        const nameSimilarity = calculateSimilarity(baseName1, baseName2);
        
        return nameSimilarity > 0.6 && (sameCategory || sameBrand);
      })?.slice(0, 4);

      if (similar?.length > 0) {
        groups?.push({
          main: product,
          similar,
          suggestedType: 'COR'
        });
        processed?.add(product?.id);
        similar?.forEach(p => processed?.add(p?.id));
      }
    });

    return groups?.slice(0, 10);
  };

  const findGroupsBySize = (products) => {
    const sizeKeywords = ['p', 'm', 'g', 'gg', 'pp', 'xg', 'xxg', 'pequeno', 'medio', 'grande', 'extra grande', 'small', 'medium', 'large', 'xl', 'xxl', 'tamanho', 'size'];
    const groups = [];
    const processed = new Set();

    products?.forEach((product, index) => {
      if (processed?.has(product?.id)) return;

      const productName = product?.name?.toLowerCase();
      const productDesc = (product?.description || '')?.toLowerCase();
      const combinedText = `${productName} ${productDesc}`;
      const hasSizeInName = sizeKeywords?.some(size => {
        const regex = new RegExp(`\\b${size}\\b`, 'i');
        return regex?.test(combinedText);
      });
      
      if (!hasSizeInName) return;

      const similar = products?.filter((p, idx) => {
        if (idx === index || processed?.has(p?.id)) return false;
        const pName = p?.name?.toLowerCase();
        const pDesc = (p?.description || '')?.toLowerCase();
        const pCombined = `${pName} ${pDesc}`;
        const hasSize = sizeKeywords?.some(size => {
          const regex = new RegExp(`\\b${size}\\b`, 'i');
          return regex?.test(pCombined);
        });
        if (!hasSize) return false;
        
        // Remove size words and compare base names
        const baseName1 = productName?.replace(new RegExp(sizeKeywords?.join('|'), 'gi'), '')?.trim();
        const baseName2 = pName?.replace(new RegExp(sizeKeywords?.join('|'), 'gi'), '')?.trim();
        
        // Also check category and brand similarity
        const sameCategory = p?.category === product?.category;
        const sameBrand = p?.brand === product?.brand;
        const nameSimilarity = calculateSimilarity(baseName1, baseName2);
        
        return nameSimilarity > 0.6 && (sameCategory || sameBrand);
      })?.slice(0, 4);

      if (similar?.length > 0) {
        groups?.push({
          main: product,
          similar,
          suggestedType: 'TAMANHO'
        });
        processed?.add(product?.id);
        similar?.forEach(p => processed?.add(p?.id));
      }
    });

    return groups?.slice(0, 10);
  };

  const findGroupsByBrand = (products) => {
    const brandGroups = {};
    
    products?.forEach(product => {
      const brand = product?.brand || 'Sem Marca';
      if (!brandGroups?.[brand]) {
        brandGroups[brand] = [];
      }
      brandGroups?.[brand]?.push(product);
    });

    const groups = [];
    Object.entries(brandGroups)?.forEach(([brand, brandProducts]) => {
      if (brandProducts?.length > 1) {
        // Group by similar names within the same brand
        const processed = new Set();
        
        brandProducts?.forEach((product, index) => {
          if (processed?.has(product?.id)) return;
          
          const similar = brandProducts?.filter((p, idx) => {
            if (idx === index || processed?.has(p?.id)) return false;
            
            // Check name similarity and category
            const nameSimilarity = calculateSimilarity(p?.name, product?.name);
            const sameCategory = p?.category === product?.category;
            
            return nameSimilarity > 0.5 || sameCategory;
          })?.slice(0, 4);
          
          if (similar?.length > 0) {
            groups?.push({
              main: product,
              similar,
              suggestedType: 'MARCA'
            });
            processed?.add(product?.id);
            similar?.forEach(p => processed?.add(p?.id));
          }
        });
      }
    });

    return groups?.slice(0, 10);
  };

  const findGroupsByCategory = (products) => {
    const categoryGroups = {};
    
    products?.forEach(product => {
      const category = product?.category || 'Sem Categoria';
      if (!categoryGroups?.[category]) {
        categoryGroups[category] = [];
      }
      categoryGroups?.[category]?.push(product);
    });

    const groups = [];
    Object.entries(categoryGroups)?.forEach(([category, categoryProducts]) => {
      if (categoryProducts?.length > 1) {
        // Group by similar names within the same category
        const processed = new Set();
        
        categoryProducts?.forEach((product, index) => {
          if (processed?.has(product?.id)) return;
          
          const similar = categoryProducts?.filter((p, idx) => {
            if (idx === index || processed?.has(p?.id)) return false;
            
            // Check name similarity and brand
            const nameSimilarity = calculateSimilarity(p?.name, product?.name);
            const sameBrand = p?.brand === product?.brand;
            
            return nameSimilarity > 0.5 || sameBrand;
          })?.slice(0, 4);
          
          if (similar?.length > 0) {
            groups?.push({
              main: product,
              similar,
              suggestedType: 'CATEGORIA'
            });
            processed?.add(product?.id);
            similar?.forEach(p => processed?.add(p?.id));
          }
        });
      }
    });

    return groups?.slice(0, 10);
  };

  const findSimilarProducts = (products) => {
    const groups = [];
    const processed = new Set();

    products?.forEach((product, index) => {
      if (processed?.has(product?.id)) return;

      const similar = products?.filter((p, idx) => {
        if (idx === index || processed?.has(p?.id)) return false;
        
        // IMPROVED SIMILARITY DETECTION
        const sameCategoryAndBrand = p?.category === product?.category && p?.brand === product?.brand;
        const similarName = calculateSimilarity(p?.name, product?.name) > 0.6;
        const similarSKU = calculateSKUSimilarity(p?.sku, product?.sku) > 0.7;
        
        return sameCategoryAndBrand && (similarName || similarSKU);
      })?.slice(0, 4);

      if (similar?.length > 0) {
        groups?.push({
          main: product,
          similar,
          suggestedType: detectVariationType(product, similar)
        });
        processed?.add(product?.id);
        similar?.forEach(p => processed?.add(p?.id));
      }
    });

    return groups?.slice(0, 10);
  };

  const calculateSimilarity = (str1, str2) => {
    if (!str1 || !str2) return 0;
    const words1 = str1?.toLowerCase()?.split(' ');
    const words2 = str2?.toLowerCase()?.split(' ');
    const common = words1?.filter(w => words2?.includes(w))?.length;
    return common / Math.max(words1?.length, words2?.length);
  };

  const calculateSKUSimilarity = (sku1, sku2) => {
    if (!sku1 || !sku2) return 0;
    const base1 = sku1?.replace(/[-_]?\d+$/, '');
    const base2 = sku2?.replace(/[-_]?\d+$/, '');
    return base1 === base2 ? 1 : 0;
  };

  const detectVariationType = (main, similar) => {
    const allNames = [main?.name, ...similar?.map(p => p?.name)]?.join(' ')?.toLowerCase();
    
    if (allNames?.includes('cor') || allNames?.includes('color') || allNames?.includes('preto') || allNames?.includes('branco')) {
      return 'COR';
    }
    if (allNames?.includes('tamanho') || allNames?.includes('size') || allNames?.includes('pequeno') || allNames?.includes('grande')) {
      return 'TAMANHO';
    }
    return 'MODELO';
  };

  const handleImproveGroupingWithAI = async () => {
    try {
      setAnalyzingWithAI(true);
      
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'info',
          message: `IA analisando produtos para ${groupingTabs?.find(t => t?.id === activeTab)?.label}...`,
          progress: 50
        }
      }));
      
      const { products } = await productService?.getAll();
      const originalProducts = products?.filter(p => {
        if (p?.productType === 'kit' || p?.product_type === 'kit') return false;
        if (p?.metadata?.groupType) return false;
        return true;
      });
      
      const productsForAnalysis = originalProducts?.slice(0, 50)?.map(p => ({
        id: p?.id,
        name: p?.name,
        sku: p?.sku,
        brand: p?.brand,
        category: p?.category,
        description: p?.description
      }));
      
      let strategyPrompt = '';
      let strategyInstructions = '';
      
      switch (activeTab) {
        case 'color':
          strategyPrompt = 'Identifique produtos que diferem apenas na cor (preto, branco, azul, vermelho, verde, amarelo, rosa, roxo, laranja, cinza, marrom, bege, etc.)';
          strategyInstructions = 'Agrupe produtos com nomes base idênticos mas cores diferentes. Exemplo: "Camiseta Básica Preta" e "Camiseta Básica Branca" devem ser agrupadas.';
          break;
        case 'size':
          strategyPrompt = 'Identifique produtos que diferem apenas no tamanho (P, M, G, GG, PP, XG, XXG, pequeno, médio, grande, etc.)';
          strategyInstructions = 'Agrupe produtos com nomes base idênticos mas tamanhos diferentes. Exemplo: "Calça Jeans M" e "Calça Jeans G" devem ser agrupadas.';
          break;
        case 'brand':
          strategyPrompt = 'Agrupe produtos da mesma marca que são similares ou complementares';
          strategyInstructions = 'Foque em produtos da mesma marca com nomes similares ou da mesma linha de produtos.';
          break;
        case 'category':
          strategyPrompt = 'Agrupe produtos da mesma categoria que são similares ou complementares';
          strategyInstructions = 'Foque em produtos da mesma categoria com características similares.';
          break;
        default:
          strategyPrompt = 'Identifique grupos de variações usando múltiplos critérios (cor, tamanho, modelo, voltagem)';
          strategyInstructions = 'Use análise inteligente para detectar qualquer tipo de variação entre produtos similares.';
      }
      
      const prompt = `Você é um especialista em análise de catálogo de produtos. ${strategyPrompt}

${strategyInstructions}

PRODUTOS:
${JSON.stringify(productsForAnalysis, null, 2)}

CRITÉRIOS:
1. Produtos com nome base similar mas diferenças em cor, tamanho, voltagem ou modelo
2. Mesmo SKU base (ex: ABC-001, ABC-002)
3. Mesma marca E categoria
4. Descrições similares

RETORNE JSON:
{
  "groups": [
    {
      "mainProductId": "id",
      "similarProductIds": ["id1", "id2"],
      "variationType": "COR" | "TAMANHO" | "VOLTAGEM" | "MODELO" | "MARCA" | "CATEGORIA",
      "confidence": "high" | "medium" | "low",
      "reason": "explicação detalhada"
    }
  ]
}`;
      
      const response = await aiService?.generateContent(prompt, { 
        maxTokens: 2000,
        responseFormat: 'json'
      });
      
      if (response?.success) {
        let result;
        try {
          result = JSON.parse(response?.content);
        } catch {
          result = { groups: [] };
        }
        
        if (result?.groups && result?.groups?.length > 0) {
          const aiGroups = result?.groups?.map(g => {
            const main = originalProducts?.find(p => p?.id === g?.mainProductId);
            const similar = g?.similarProductIds?.map(id => 
              originalProducts?.find(p => p?.id === id)
            )?.filter(Boolean);
            
            return {
              main,
              similar,
              suggestedType: g?.variationType,
              aiConfidence: g?.confidence,
              aiReason: g?.reason
            };
          })?.filter(g => g?.main && g?.similar?.length > 0);
          
          setGroups(aiGroups);
          
          const totalCount = aiGroups?.reduce((sum, group) => {
            return sum + 1 + (group?.similar?.length || 0);
          }, 0);
          setDetectedCount(totalCount);
          
          window.dispatchEvent(new CustomEvent('statusUpdate', {
            detail: {
              type: 'success',
              message: `IA encontrou ${aiGroups?.length} grupos com ${totalCount} produtos`,
              progress: 100
            }
          }));
        } else {
          window.dispatchEvent(new CustomEvent('statusUpdate', {
            detail: {
              type: 'info',
              message: 'IA não encontrou novos agrupamentos',
              progress: 100
            }
          }));
        }
      } else {
        throw new Error(response?.error || 'IA não disponível');
      }
    } catch (error) {
      console.error('Error analyzing with AI:', error);
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'error',
          message: error?.message || 'Erro ao analisar com IA',
          progress: 0
        }
      }));
    } finally {
      setAnalyzingWithAI(false);
    }
  };

  const handleGroupAutomatically = async (group) => {
    try {
      setGrouping(group?.main?.id);
      
      // Collect all images and SKUs from all products in the group
      const allImages = [];
      const allSkus = [];
      
      // Add main product images and SKU
      if (group?.main?.images && Array.isArray(group?.main?.images)) {
        allImages?.push(...group?.main?.images);
      }
      if (group?.main?.sku) {
        allSkus?.push(group?.main?.sku);
      }
      
      // Add similar products images and SKUs
      group?.similar?.forEach(product => {
        if (product?.images && Array.isArray(product?.images)) {
          allImages?.push(...product?.images);
        }
        if (product?.sku) {
          allSkus?.push(product?.sku);
        }
      });
      
      // Remove duplicate images
      const uniqueImages = [...new Set(allImages)];
      
      // Create a new grouped product with all images and SKUs
      const groupedProduct = {
        name: `${group?.main?.name} (Agrupado)`,
        sku: `GROUP-${Date.now()}-${Math.random()?.toString(36)?.substring(2, 7)?.toUpperCase()}`,
        productType: 'product',
        price: group?.main?.price,
        description: `Produto agrupado com ${group?.similar?.length + 1} variações`,
        images: uniqueImages,
        status: 'draft',
        category: group?.main?.category,
        metadata: {
          groupType: group?.suggestedType,
          allSkus: allSkus,
          mainProduct: {
            id: group?.main?.id,
            name: group?.main?.name,
            price: group?.main?.price,
            sku: group?.main?.sku,
            images: group?.main?.images || []
          },
          variations: group?.similar?.map(p => ({
            id: p?.id,
            name: p?.name,
            price: p?.price,
            sku: p?.sku,
            images: p?.images || []
          }))
        }
      };

      await productService?.create(groupedProduct);

      // Trigger auto-reload
      triggerAutoReload('grouped-product-created');

      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'success',
          message: `Grupo "${groupedProduct?.name}" criado com sucesso!`,
          progress: 100
        }
      }));

      // Remove grouped items from suggestions
      setGroups(prevGroups => prevGroups?.filter(g => g?.main?.id !== group?.main?.id));
      
      // Navigate to Produtos Agrupados tab
      setTimeout(() => {
        navigate('/product-catalog-manager?tab=grouped');
      }, 800);
    } catch (error) {
      console.error('Error grouping variations:', error);
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'error',
          message: 'Erro ao agrupar variações',
          details: error?.message
        }
      }));
    } finally {
      setGrouping(null);
    }
  };

  const handleDeleteGroup = async (group) => {
    const confirmMessage = `Tem certeza que deseja excluir este grupo de sugestão?\n\nProduto principal: ${group?.main?.name}\n${group?.similar?.length} produtos similares\n\nEsta ação não pode ser desfeita!`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setDeleting(group?.main?.id);
      
      // Remove from local state
      setGroups(prevGroups => prevGroups?.filter(g => g?.main?.id !== group?.main?.id));
      
      // Update detected count
      const removedCount = 1 + (group?.similar?.length || 0);
      setDetectedCount(prev => Math.max(0, prev - removedCount));
      
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'success',
          message: 'Grupo removido da lista de sugestões',
          progress: 100
        }
      }));
    } catch (error) {
      console.error('Error deleting group:', error);
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'error',
          message: 'Erro ao excluir grupo',
          progress: 0
        }
      }));
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteAll = async () => {
    const tabLabel = groupingTabs?.find(t => t?.id === activeTab)?.label;
    const confirmMessage = `⚠️ ATENÇÃO: Esta ação irá excluir TODAS as ${groups?.length} sugestões de agrupamento da aba "${tabLabel}" SEM EXCEÇÃO.\n\nEsta ação NÃO pode ser desfeita!\n\nDigite "EXCLUIR TUDO" para confirmar:`;
    
    const userInput = window.prompt(confirmMessage);
    
    if (userInput !== 'EXCLUIR TUDO') {
      if (userInput !== null) {
        window.dispatchEvent(new CustomEvent('statusUpdate', {
          detail: {
            type: 'info',
            message: 'Exclusão cancelada - confirmação incorreta',
            progress: 0
          }
        }));
      }
      return;
    }

    try {
      setDeleting('all');
      
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'info',
          message: `Excluindo ${groups?.length} grupos de sugestões...`,
          progress: 50
        }
      }));
      
      // Clear all groups
      setGroups([]);
      setDetectedCount(0);
      
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'success',
          message: `${groups?.length} grupos excluídos com sucesso!`,
          progress: 100
        }
      }));
    } catch (error) {
      console.error('Error deleting all groups:', error);
      window.dispatchEvent(new CustomEvent('statusUpdate', {
        detail: {
          type: 'error',
          message: 'Erro ao excluir todos os grupos',
          progress: 0
        }
      }));
    } finally {
      setDeleting(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Icon name="Loader" size={40} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Navigation Tabs */}
      <div className="border-b border-border mb-6">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {groupingTabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-t-lg font-medium text-sm transition-all whitespace-nowrap ${
                activeTab === tab?.id
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h3 className="text-xl font-semibold text-foreground mb-1">
              {groupingTabs?.find(t => t?.id === activeTab)?.label}
            </h3>
            <p className="text-sm text-muted-foreground">
              {groupingTabs?.find(t => t?.id === activeTab)?.description}
            </p>
          </div>
          <div className="flex gap-2">
            {groups?.length > 0 && (
              <Button
                onClick={handleDeleteAll}
                disabled={deleting === 'all'}
                variant="destructive"
                size="sm"
                className="bg-red-600 hover:bg-red-700"
              >
                {deleting === 'all' ? (
                  <>
                    <Icon name="Loader" size={14} className="mr-2 animate-spin" />
                    Excluindo...
                  </>
                ) : (
                  <>
                    <Icon name="Trash" size={14} className="mr-2" />
                    Excluir Todos ({groups?.length})
                  </>
                )}
              </Button>
            )}
            <Button
              onClick={handleImproveGroupingWithAI}
              disabled={analyzingWithAI}
              variant="primary"
              size="sm"
            >
              {analyzingWithAI ? (
                <>
                  <Icon name="Loader" size={14} className="mr-2 animate-spin" />
                  Analisando...
                </>
              ) : (
                <>
                  <Icon name="Sparkles" size={14} className="mr-2" />
                  Melhorar com IA
                </>
              )}
            </Button>
          </div>
        </div>
        {detectedCount > 0 && (
          <div className="px-3 py-1 bg-blue-500/10 text-blue-600 rounded-lg text-sm font-semibold inline-block">
            {detectedCount} produtos detectados em {groups?.length} grupos
          </div>
        )}
      </div>

      {groups?.length === 0 ? (
        <div className="text-center py-16">
          <Icon name="Grid" size={56} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-lg text-muted-foreground mb-2">Nenhum grupo de variação encontrado</p>
          <p className="text-sm text-muted-foreground mb-6">Não há produtos similares suficientes para agrupar</p>
          <Button onClick={loadVariationGroups} variant="outline">
            <Icon name="RefreshCw" size={16} className="mr-2" />
            Recarregar
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {groups?.map((group, index) => (
            <div key={index} className="bg-background border border-border rounded-xl p-6 hover:shadow-lg transition-smooth">
              {/* Main Product */}
              <div className="flex items-center gap-4 mb-4 pb-4 border-b border-border">
                <div className="w-20 h-20 bg-muted rounded-lg overflow-hidden">
                  {group?.main?.images?.[0] ? (
                    <img src={group?.main?.images?.[0]} alt={group?.main?.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name="Package" size={28} className="text-muted-foreground" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-base text-foreground mb-1">{group?.main?.name}</p>
                  <p className="text-xs text-muted-foreground">Produto Principal</p>
                </div>
              </div>

              {/* AI Confidence Badge */}
              {group?.aiConfidence && (
                <div className="mb-3">
                  <span className={`inline-block px-3 py-1 rounded-lg text-xs font-medium ${
                    group?.aiConfidence === 'high' ? 'bg-green-500/10 text-green-600' :
                    group?.aiConfidence === 'medium'? 'bg-yellow-500/10 text-yellow-600' : 'bg-gray-500/10 text-gray-600'
                  }`}>
                    🤖 IA: Confiança {group?.aiConfidence === 'high' ? 'Alta' : group?.aiConfidence === 'medium' ? 'Média' : 'Baixa'}
                  </span>
                  {group?.aiReason && (
                    <p className="text-xs text-muted-foreground mt-2">{group?.aiReason}</p>
                  )}
                </div>
              )}

              {/* Similar Products */}
              <div className="space-y-3 mb-4">
                <p className="text-sm font-medium text-muted-foreground">Produtos Similares ({group?.similar?.length})</p>
                <div className="flex flex-wrap gap-3">
                  {group?.similar?.map((product, idx) => (
                    <div key={idx} className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2">
                      <div className="w-10 h-10 bg-muted rounded overflow-hidden">
                        {product?.images?.[0] ? (
                          <img src={product?.images?.[0]} alt={product?.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Icon name="Package" size={14} className="text-muted-foreground" />
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-foreground truncate max-w-[140px]">{product?.name}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Suggestion */}
              <div className="bg-info/10 border border-info/20 rounded-lg p-4 mb-4">
                <p className="text-sm text-foreground">
                  💡 Sugestão: Agrupar como variações de <strong>{group?.suggestedType}</strong>
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  onClick={() => handleGroupAutomatically(group)}
                  variant="primary"
                  className="flex-1 bg-slate-700 hover:bg-slate-800 text-white"
                  disabled={grouping === group?.main?.id || deleting === group?.main?.id}
                >
                  {grouping === group?.main?.id ? (
                    <>
                      <Icon name="Loader" size={18} className="mr-2 animate-spin" />
                      Agrupando...
                    </>
                  ) : (
                    <>
                      <Icon name="Link" size={18} className="mr-2" />
                      🔗 Agrupar
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => handleDeleteGroup(group)}
                  variant="destructive"
                  size="default"
                  disabled={grouping === group?.main?.id || deleting === group?.main?.id}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {deleting === group?.main?.id ? (
                    <Icon name="Loader" size={18} className="animate-spin" />
                  ) : (
                    <Icon name="Trash" size={18} />
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GroupVariationsTab;